import { useState } from "react";
import { toast } from "sonner";
import { WarningDiamond } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { DEPLOYMENT_HELP, INTERFACE_HELP } from "@/lib/glossary";

const label = (v) => String(v || "").replace(/_/g, " ");

const FIELD_LABELS = {
  product_version: "Product version",
  deployment_model: "Deployment model",
  interfaces: "External interfaces",
};

export const CompleteScopeDialog = ({ product, meta, missing, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    product_version: product.product_version || "",
    firmware_version: product.firmware_version || "",
    deployment_model: product.deployment_model === "not_stated" ? "" : product.deployment_model || "",
    description: product.description || "",
    interfaces: product.interfaces || [],
  });

  const toggleInterface = (i) =>
    setForm((f) => ({
      ...f,
      interfaces: f.interfaces.includes(i) ? f.interfaces.filter((x) => x !== i) : [...f.interfaces, i],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.deployment_model) delete payload.deployment_model;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" ) delete payload[k];
      });
      const { data } = await api.patch(`/products/${product.product_id}`, payload);
      toast.success(
        data.missing_scope_fields.length
          ? `Saved. Still required: ${data.missing_scope_fields.map((m) => FIELD_LABELS[m]).join(", ")}`
          : "Scope details complete"
      );
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="border border-amber-300 bg-amber-50 p-5" data-testid="scope-required-banner">
        <div className="flex gap-3">
          <WarningDiamond size={20} className="mt-0.5 shrink-0 text-amber-700" />
          <div>
            <p className="font-display text-sm font-bold text-amber-900">
              Complete the product scope before unlocking the report
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-amber-800">
              The report must state exactly what was assessed. Still required:{" "}
              <strong>{missing.map((m) => FIELD_LABELS[m]).join(", ")}</strong>.
            </p>
            <Button
              size="sm"
              className="mt-3 bg-navy-800 text-white hover:bg-navy-700"
              onClick={() => setOpen(true)}
              data-testid="open-scope-dialog-button"
            >
              Complete scope details
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="scope-dialog">
          <DialogHeader>
            <DialogTitle>Product scope details</DialogTitle>
            <DialogDescription>
              These appear in the report's Assessment Scope section and tie every finding to a specific build.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Product version *</Label>
                <Input
                  value={form.product_version}
                  placeholder="e.g. Rev C"
                  onChange={(e) => setForm({ ...form, product_version: e.target.value })}
                  data-testid="scope-product-version-input"
                />
              </div>
              <div>
                <Label className="text-xs">Firmware version</Label>
                <Input
                  value={form.firmware_version}
                  placeholder="e.g. 4.2.1"
                  onChange={(e) => setForm({ ...form, firmware_version: e.target.value })}
                  data-testid="scope-firmware-version-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Deployment model *</Label>
              <Select value={form.deployment_model} onValueChange={(v) => setForm({ ...form, deployment_model: v })}>
                <SelectTrigger data-testid="scope-deployment-select">
                  <SelectValue placeholder="Select deployment model" />
                </SelectTrigger>
                <SelectContent className="max-w-[360px]">
                  {(meta?.deployment_models || []).filter((d) => d !== "not_stated").map((d) => (
                    <SelectItem key={d} value={d} data-testid={`scope-deployment-option-${d}`}>
                      <span className="block max-w-[320px] whitespace-normal break-words">
                        <span className="text-slate-800">{label(d)}</span>
                        <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-500">
                          {DEPLOYMENT_HELP[d]}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.deployment_model && (
                <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500" data-testid="scope-deployment-hint">
                  {DEPLOYMENT_HELP[form.deployment_model]}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">External interfaces * (at least one)</Label>
              <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                {(meta?.interfaces || []).map((i) => (
                  <label key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Checkbox
                      checked={form.interfaces.includes(i)}
                      onCheckedChange={() => toggleInterface(i)}
                      data-testid={`scope-interface-${i}`}
                    />
                    <span>
                      {label(i)}
                      <span className="block text-[11px] leading-relaxed text-slate-500">
                        {INTERFACE_HELP[i]}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Short description</Label>
              <Input
                value={form.description}
                placeholder="What the product does and where it is deployed"
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="scope-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              className="bg-navy-800 text-white hover:bg-navy-700"
              disabled={saving}
              onClick={save}
              data-testid="save-scope-button"
            >
              {saving ? "Saving…" : "Save scope details"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ScopeValidationWarnings = ({ warnings, testId = "scope-validation-warnings" }) => {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid={testId}>
      <p className="overline text-navy-800">Scope validation</p>
      <ul className="mt-4 space-y-3">
        {warnings.map((w) => (
          <li key={w.code} className="flex gap-2.5" data-testid={`scope-warning-${w.code}`}>
            <span
              className={`mt-0.5 h-fit border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                w.level === "warning"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              {w.level}
            </span>
            <div>
              <p className="text-sm font-semibold text-navy-900">{w.message}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{w.detail}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
